import * as React from 'react';
import {useEffect, useState} from 'react';
import classnames from 'classnames';
import isPast from 'date-fns/isPast';
import isFuture from 'date-fns/isFuture';
import {FormattedMessage} from 'react-intl';
import { InView } from 'react-intersection-observer';

import Sticky from 'react-stickynode';

import {convertToSearchResult} from './helpers';
import {SITE_URLS} from 'features/search/constants';
import {LOCALE, SITE, SiteId, LinkStyle} from 'features/ui/enums';
import {isVipPeriod} from 'features/services/utils/date-helpers';
import {SearchService} from 'features/search/services/SearchService';
import {capitalizeFirstLetter} from 'features/services/utils/helpers';
import {LoginForm} from 'components/forms/LoginForm';
import {getObjectImagesByType} from 'features/services/utils/image-helpers';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {hasShowing} from 'features/services/utils/showing-helpers';
import {getArticlesByTag} from 'features/services/pageservice/pageservice-api';
import {sanitizeHtml} from 'features/services/utils/sanitize-html';
import {PriceListV2} from "features/residenceExplorer/components/PriceListV2";
import {CookieStore} from 'features/cookies/cookie-store';
import {useResidenceExplorerStore, selectExplorerObjects} from "features/residenceExplorer/store/useResidenceExplorerStore";
import {ResidenceExplorerV2} from "features/residenceExplorer/components/ResidenceExplorerV2";
import {
    useUiStore,
    selectInterestModalVisibility,
    selectRedirectMessageVisibility,
} from 'features/ui/store/useUiStore';
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import {ApartmentViewerPlayer} from "features/apartmentViewer/components/ApartmentViewerPlayer";
import {checkIfBookable, getBuyResidenceCookie} from '@organisms/BuyResidenceGeneral/services/api';
import {ErrorService} from 'features/errors';
import {convertFiltersForApi, getSiteIdForQuery, initialFilterState} from 'features/search/helpers';
import {gtmEventsObjectPageStickyClick, gtmEventsPurchaseSignupOpen} from 'features/services/googleTagManager/events';
import {
    getCurrentLocale,
    getCurrentSite,
    getLocalizedKvm,
    getLocalizedRok,
    getSiteSpecificConfig
} from 'features/services/utils/env-helper';
import {
    ConceptType,
    DisposalForm,
    EidentError,
    ObjectStatus,
    ResidenceType,
    SortBy,
    StageStatus
} from 'features/search/filters/enums';

import './style.scss';

import {A} from '@atoms/LinksAndButtons/A';
import {P} from '@atoms/Text/P';
import {MainGrid} from '@atoms/Layout/MainGrid';
import {Container} from '@atoms/Layout/Container';
import {UserAgent} from '@atoms/UserAgent';
import {ContentArea} from '@organisms/GeneralComponents/ContentArea';
import {InfoMessage} from '@molecules/Banners/InfoMessage';
import {MapWrapper} from '@organisms/Map/MapWrapper';

import {StructuredData} from '@molecules/StructuredData';
import {CoOwningBanner} from '@molecules/Banners/CoOwningBanner';
import {VipPeriodWindow} from '@organisms/VipPeriodWindow';
import {FloorplanTabber} from '@organisms/FloorplanTabber';
import {VipPeriodBanner} from '@molecules/Banners/VipPeriodBanner';
import {RedirectMessage} from '@molecules/ResidenceSearchComponents/RedirectMessage';
import {ProjectTimeline} from '@molecules/SlidingContent/ProjectTimeline';
import {ClickToBuyBanner} from '@molecules/Banners/ClickToBuyBanner';
import {Concept} from '@organisms/ResidenceSections/_concept';
import {AccessibleSlider} from '@molecules/SlidingContent/AccessibleSlider';
import {SiteSpecificContent} from '@atoms/SiteSpecificContent';
import {MainInfo, QuickFactsType} from '@organisms/ResidenceSections/_mainInfo';
import {CampaignBlock} from 'components/blocks/Text/CampaignBlock';
import {LabelSection} from '@organisms/ResidenceSections/_labels';
import {InterestFormModal} from 'components/modals/General/InterestFormModal';
import {ResidenceAside} from '@organisms/ResidenceSections/_residenceAside';
import {ImageHeaderMenu} from '@organisms/ContentBlocks/ImageHeader/ImageHeaderMenu';
import {BookingButton} from '@organisms/ContentBlocks/ImageHeader/ResidenceActionButton/_bookingButton';
import {InterestButton} from '@organisms/ContentBlocks/ImageHeader/ResidenceActionButton/_interestButton';
import {PurchaseButton} from '@organisms/ContentBlocks/ImageHeader/ResidenceActionButton/_purchaseButton';
import {useBreadcrumbs} from "features/template/ViewModelProvider";
import {useMetaStore, selectCookieSettings} from 'features/meta/store/useMetaStore';
import {ImageHeader} from "@organisms/ContentBlocks/ImageHeader";
import {ImageGallery} from "@organisms/ContentBlocks/ImageGallery";
import {ResidenceExplorerPageType} from "features/residenceExplorer/types";
import {useNavigate} from 'react-router';
import {SearchResult} from "features/search/types";
import {GalleryType, PageModel} from "features/template/types";
import {ResidenceObjectViewModel} from "features/template/viewModels/ResidenceObject";
import {ObjectImageType} from 'features/template/viewModels';

import {useAuth} from "features/authentication/hooks/useAuth";
import {InterestForm} from "components/forms/InterestForm";
import {getEidentError} from "features/authentication/helpers";
import {useApartmentViewerStore, selectNoViews} from "features/apartmentViewer/store/useApartmentViewerStore";

import {ExternalExplorerBlock} from "features/residenceExplorer/components/ExternalExplorerBlock";
import {ModalPosition, useModal} from "features/modal/hooks/useModal";
import { ResidenceCardType, ResidenceCard } from '@molecules/ResidentialCards/ResidenceCard';

type Props = PageModel<ResidenceObjectViewModel>;

// Statusar där bostaden inte längre går att boka. Backend 404:ar bokningssidan för sålda objekt
// (BuyResidenceController.IsObjectAvailable), så bokningsbannern får inte visas för dem heller.
const UNBOOKABLE_OBJECT_STATUSES: readonly string[] = [
    ObjectStatus.SOLD,
    ObjectStatus.BOOKED,
    ObjectStatus.RESERVED,
];

export const ObjectPage: React.FunctionComponent<Props> = ({
    viewModel,
}) => {
    const { open } = useModal();

    const isMobile = useDeviceStore(selectIsMobile);
    const breadcrumbs = useBreadcrumbs();
    const navigate = useNavigate();
    const siteId = getSiteSpecificConfig().siteId;
    const userCookieSettings = useMetaStore(selectCookieSettings);
    const showRedirectMessage = useUiStore(selectRedirectMessageVisibility);
    const noViews = useApartmentViewerStore(selectNoViews);

    const {user, isLoggedIn} = useAuth();

    const statusAllowsBooking = !UNBOOKABLE_OBJECT_STATUSES.includes(viewModel.objectStatus);

    const interestModalVisibility = useUiStore(selectInterestModalVisibility);
    const setInterestModalVisibility = useUiStore((state) => state.setInterestModalVisibility);

    const [bellowFold, setBellowFold] = useState<boolean>(false);
    const [isBookable, setIsBookable] = useState<boolean>(false);
    const [showInterestButton, setShowInterestButton] = useState<boolean>(true);
    const [interestDone, setInterestDone] = useState<boolean>(false);
    // Stage & objects
    const [areaName, setAreaName] = useState<string>('');
    const [relatedObjects, setRelatedObjects] = useState<SearchResult[]>([]);
    const [relatedObjectsUrl, setRelatedObjectsUrl] = useState<string>('');
    const [connectedStages, setConnectedStages] = useState<SearchResult[]>([]);
    const [purchaseComplete, setPurchaseComplete] = useState<boolean>(false);

    const objects = useResidenceExplorerStore(selectExplorerObjects);

    const scrollRefs = {
        overview: React.createRef<HTMLDivElement>(),
        threeSixty: React.createRef<HTMLDivElement>(),
        showings: React.createRef<HTMLDivElement>(),
        facts: React.createRef<HTMLDivElement>(),
        tabbedSection: React.createRef<HTMLDivElement>(),
        floorPlan: React.createRef<HTMLDivElement>(),
        interest: React.createRef<HTMLDivElement>(),
        images: React.createRef<HTMLDivElement>(),
        objects: React.createRef<HTMLDivElement>(),
        about: React.createRef<HTMLDivElement>(),
        map: React.createRef<HTMLDivElement>(),
    };

    useEffect(() => {
        setupObject();

        return () => {
            setInterestModalVisibility(false);
        }
    }, []);

    useEffect(() => {
        if(!user) return;

        if(interestModalVisibility) {
            open(<InterestFormModal
                stageName={viewModel.header}
                agent={viewModel.realEstateAgents && viewModel.realEstateAgents[0]}
                stageId={viewModel.stage}
                objectId={viewModel.objectId}
                stageStatus={viewModel.stageStatus}
                interestType="default"
                interestDone={interestDone}
                setInterestDone={setInterestDone}
                hideMessageField={viewModel.stageStatus === StageStatus.PLANNED || viewModel.stageStatus === StageStatus.VIP}
                basicSignupPulText={viewModel.basicSignupPulText}
                labelPrefix="modal-"
            />, { fullscreen: false, position: ModalPosition.RIGHT, closeCallback: () => setInterestModalVisibility(false), ariaLabel: getFormattedMessage('jm.components.InterestForm.header') });
        }
    }, [user]);

    const setupObject = async () => {
        CookieStore.setSourceMedium();

        const [relatedObjects, bookableAccordingToApi, connectedStages, bookableAccordingToCookie] = await Promise.all([
            getRelatedObjects(),
            checkIfBookable(viewModel.objectId, true).catch((e: unknown) => {
                ErrorService.report(e, { source: 'object-page', action: 'check-bookable' });
                return false;
            }),
            getConnectedStages(),
            checkCookieStatus(),
        ]);

        const url = new URL(window.location.href);
        const paths = url.pathname.split('/').filter((el) => el !== '');
        let countyUrl = SITE_URLS.searchUrl;

        if(getCurrentSite() !== SITE.FI) {
            countyUrl += paths[0] + '/#object';
        } else{
            countyUrl += paths[1] + '/#object';
        }

        setRelatedObjects(relatedObjects);
        setIsBookable(statusAllowsBooking && (bookableAccordingToApi || bookableAccordingToCookie));
        setRelatedObjectsUrl(countyUrl);
        setConnectedStages(connectedStages);
        setAreaName(getAreaName());

        const hash = window.location.hash.replace('#', '');
        if (typeof scrollRefs[hash] === 'undefined') {
            /* setTimeout(() => {
                window.scroll({
                    top: 0,
                    behavior: 'auto',
                });
            }); */
        } else {
            setTimeout(() => {
                scrollToReference(scrollRefs[hash]);
            }, 500);
        }

        if (isVipPeriod(viewModel.stageStatus, viewModel.vipEnd)) {
            if (window.location.hash === '#purchase-success') {
                onVipPeriodClick(true);
            }
            if(window.location.hash && window.location.hash.startsWith('#eident-error')) {
                const eidentError = getEidentError(window.location.hash);
                onVipPeriodClick(false, eidentError);
            }
        } else {
            if (window.location.hash === '#purchase-success') {
                onForSalePeriodClick(true);
            }
            if (window.location.hash && window.location.hash.startsWith('#eident-error')) {
                const eidentError = getEidentError(window.location.hash);
                onForSalePeriodClick(false, eidentError);
            }
        }
    };

    useEffect(() => {
        if(window.location.hash.indexOf('objects') > -1) {
            scrollToReference(scrollRefs.objects, true);
        }
        if(window.location.hash.indexOf('about') > -1) {
            scrollToReference(scrollRefs.about, true);
        }
        if(window.location.hash.indexOf('facts') > -1) {
            scrollToReference(scrollRefs.facts, true);
        }
        if(window.location.hash.indexOf('images') > -1) {
            scrollToReference(scrollRefs.images, true);
        }
        if(window.location.hash.indexOf('map') > -1) {
            scrollToReference(scrollRefs.map, true);
        }
        if(window.location.hash.indexOf('overview') > -1) {
            scrollToReference(scrollRefs.overview, true);
        }
        if(window.location.hash.indexOf('showings') > -1) {
            scrollToReference(scrollRefs.showings, true);
        }
        if(window.location.hash.indexOf('interest') > -1) {
            scrollToReference(scrollRefs.interest, true);
        }
    }, [window.location.hash]);

    useEffect(() => {
        if(user?.isVip) {
            setupRelatedObjects();
        }
    }, [user]);
    const setupRelatedObjects = async () => {
        const tempRelatedObjects = await getRelatedObjects();
        setRelatedObjects(tempRelatedObjects);
    };

    const checkCookieStatus = async () => {
        const jsonModel = await getBuyResidenceCookie(); //CookieStore.getClickToBuyModel(this.props.viewModel.objectId);

        if(jsonModel && jsonModel.objectId === viewModel.objectId) {
            if(getCurrentSite() === SITE.NO && !jsonModel.contractId) {
                return false;
            }
            //return jsonModel.objectId === this.props.viewModel.objectId && typeof jsonModel.signing.signees !== 'undefined' && jsonModel.signing.signees.filter(x => x.signed === true).length > 0 && this.props.viewModel.objectStatus !== ObjectStatus.BOOKED;
            return statusAllowsBooking;
        }

        return false;
    };

    const getAreaName = () => {
        const areaTag = viewModel.tags.find(x => x.type === "area");

        if(!areaTag) return '';
        return areaTag.name;
    };

    const getConnectedStages = async () => {
        if(!viewModel.projectId) return [];

        const apiFilters = convertFiltersForApi(initialFilterState);
        const response = await SearchService.fetchStages({
            filter: {
                ...apiFilters,
                project: viewModel.projectId,
                excludeStageIds: [viewModel.stage],
            },
            sort: ['sortOrder:asc']
        });

        return response.data?.hits ?? [];
    };

    const getRelatedObjects = async () => {
        const response = await SearchService.fetchObjects({
            minRooms: viewModel.rooms,
            maxRooms: viewModel.rooms,
            geoSearch: {
                lat: viewModel.latitude,
                lng: viewModel.longitude,
            },
            siteId: getSiteIdForQuery(),
            objectStatus: [ObjectStatus.FOR_SALE, ObjectStatus.RESERVED, ObjectStatus.PRESELLING],
        }, SortBy.ACCESS_ASC,0, 11);

        if(!response.data?.hits?.length) return [];

        return response.data.hits.filter(x => x.id !== viewModel.objectId);
    };

    const scrollToReference = (reference: React.RefObject<HTMLElement | null>, smooth: boolean = true) => {
        if (reference.current === null) return;

        // Define the focusable elements selectors
        const header = 'h1, h2';
        const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

        // Find the first focusable element within the div
        const firstFocusable = reference.current.querySelector<HTMLElement>(header);
        const secondFocusable = reference.current.querySelector<HTMLElement>(focusableSelectors);

        // Set focus if a focusable element is found
        if (firstFocusable) {
            // Headings aren't focusable by default, so .focus() is a no-op and keyboard
            // focus stays in the menu. Make the target programmatically focusable and move
            // focus to it; preventScroll leaves positioning to our smooth scroll below
            // instead of the browser's instant focus scroll.
            firstFocusable.setAttribute('tabindex', '-1');
            firstFocusable.focus({preventScroll: true});
        } else if (secondFocusable) {
            secondFocusable.focus({preventScroll: true});
        } else {
            reference.current.setAttribute('tabindex', '-1');
            reference.current.focus({preventScroll: true});
        }

        window.scroll({
            top: (reference.current.getBoundingClientRect().top + window.scrollY) - (isMobile ? 78 : 110), //reference.current.offsetTop - 100,
            behavior: smooth ? 'smooth' : 'auto',
        });
    };

    const onMenuClick = (target: string, _activeitem: number) => (e: React.SyntheticEvent) => {
        e.preventDefault();

        scrollToReference(scrollRefs[target]);
    };

    const onVipPeriodClick = (_doneState: boolean = false, _eidentError?: EidentError) => {
        if (!isLoggedIn) {
            const text = viewModel.stageStatus !== StageStatus.VIP && getCurrentLocale() === LOCALE.NO ?
                getFormattedMessage('jm.components.StagePage.purchaseModalTextTillbud') :
                getFormattedMessage('jm.components.StagePage.purchaseModalText');

            open(<LoginForm
                title={getFormattedMessage('jm.components.StagePage.purchaseModalHeadline')}
                text={text}
                hideTypeToggle
            />, {fullscreen: false, ariaLabel: getFormattedMessage('jm.components.LoginForm.ariaLabel')});

            return;
        }

        if(!user) return;

        gtmEventsPurchaseSignupOpen()();

        if(getCurrentSite() === SITE.NO) {

            //navigate(window.location.origin + `/kjopetilbud/${viewModel.objectId}`);

            window.location.href = `/kjopetilbud/${viewModel.objectId}`;
            return;
        }

        open(<VipPeriodWindow
            residence={viewModel}
            stageUrl={viewModel.stageUrl}
            purchaseSignupTermsText={viewModel.purchaseSignupTermsText}
            vipPurchaseSignupModalText={viewModel.vipPurchaseSignupModalText}
        />, {fullscreen: false, closeWithoutHistory: true, noOutsideClick: true, ariaLabel: getFormattedMessage('jm.components.VipPeriodWindow.ariaLabel')});
    };

    const gtmFuncPurchaseSignup = () => {
        gtmEventsPurchaseSignupOpen()();
    };

    const onForSalePeriodClick = (_doneState: boolean = false, _eidentError?: EidentError) => {
        if (!user) return;
        navigate({
            pathname: `/kjopetilbud/${viewModel.objectId}`,
        });
    };

    const _setInterestButtonVisibility = (visible: boolean) => {
        if (!isMobile) return;
        setShowInterestButton(visible);
        /*this.setState({
            showInterestButton: visible
        });*/
    };

    const getResidenceSearchResult = () => {
        const result = {} as SearchResult;

        result.type = "object";
        result.stage = viewModel.stage;
        result.id = viewModel.objectId;

        return result;
    };

    const _hasPurchaseFunc = () => {
        if (user && user.purchases && user.purchases.length > 0) {
            return user.purchases.filter(purchase => purchase.objectId === viewModel.objectId).length > 0;
        }
        return false;
    };

    const showInterestForm = () => {
        const vipPeriod = viewModel.stageStatus === StageStatus.VIP && isFuture(new Date(viewModel.vipEnd));

        if (user?.isLegalEntity) return false;
        if (viewModel.objectStatus === ObjectStatus.SOLD || viewModel.objectStatus === ObjectStatus.BOOKED) return false;
        if (vipPeriod && user?.isVip) return false;
        if (viewModel.isMyBookedObject) return false;
        if (viewModel.stageStatus === StageStatus.SUCCESSION) return false;

        return true;
    };

    const getResidenceType = () => {
        return typeof viewModel.residenceType !== 'undefined' ?
            viewModel.residenceType as ResidenceType :
            viewModel.constructionForm as ResidenceType;
    };

    const getResidenceHeadline = () => {
        if(getCurrentSite() === SITE.FI && typeof viewModel.jmoyNumberOfRoomsExtraInfo !== 'undefined' && viewModel.jmoyNumberOfRoomsExtraInfo !== null) {
            return `${viewModel.jmoyNumberOfRoomsExtraInfo}, ${getLocalizedKvm(viewModel.primaryArea)}`;
        }

        const residenceType = getResidenceType();
        if (residenceType == ResidenceType.PLOT) {
            return getLocalizedKvm(viewModel.plotArea);
        }

        if(getCurrentSite() === SITE.NO && viewModel.jmnoBraInternal) {
            return getFormattedMessage('jm.components.ObjectPage.headline', {
                roomsWithUnit: getLocalizedRok(viewModel.rooms),
                areaWithUnit: getLocalizedKvm(viewModel.jmnoBraInternal)
            })
        }

        return getFormattedMessage('jm.components.ObjectPage.headline', {
            roomsWithUnit: getLocalizedRok(viewModel.rooms),
            areaWithUnit: getLocalizedKvm(viewModel.primaryArea)
        })
    };

    const _getArticleTeasers = async () => {
        const data = await getArticlesByTag(siteId, viewModel.tags);
        return data != null ? data.results : [];
    };

    const bellowFoldCallback = (isVisible: boolean) => {
        if (bellowFold || !isVisible) return;
        setBellowFold(true);

        //Removed event call
        //Better not to do anything that is affecting bounce rate if we do not need to compare it anymore
        //gtmEventsPageScroll(window.location.pathname)();
    };

    const getIsVipPeriod = () => {
        if(viewModel.objectStatus === ObjectStatus.SOLD || viewModel.objectStatus === ObjectStatus.BOOKED) {
            return false;
        }

        return isVipPeriod(viewModel.stageStatus, viewModel.vipEnd) && isLoggedIn;
    };

    const getButton = () => {
        if(viewModel.isMyBookedObject) return null;

        if (viewModel.stageStatus === StageStatus.SUCCESSION) return null;

        if(isBookable && siteId === SiteId.JMFI) return <BookingButton objectId={viewModel.objectId} />;

        if(vipPeriod) return <PurchaseButton
            onClick={onVipPeriodClick}
            gtmFunc={gtmFuncPurchaseSignup}
            object={viewModel}
            setPurchaseComplete={setPurchaseComplete}
        />;

        if(viewModel.objectStatus === ObjectStatus.SOLD || viewModel.objectStatus === ObjectStatus.BOOKED || user?.hasPurchase(viewModel.objectId) || !showInterestButton) return null;

        return <InterestButton
            isVipperiod={vipPeriod}
            stageName={viewModel.header}
            agent={viewModel.realEstateAgents && viewModel.realEstateAgents[0]}
            stageId={viewModel.stage}
            stageStatus={viewModel.stageStatus}
            interestDone={interestDone}
            objectId={viewModel.objectId}
            setInterestDone={setInterestDone}
            hideMessageField={viewModel.stageStatus === StageStatus.PLANNED || viewModel.stageStatus === StageStatus.VIP}
            basicSignupPulText={viewModel.basicSignupPulText}
        />;
    };

    const informationBoxActive = viewModel.informationBoxShowOnObjects && (viewModel.informationBoxText != null || viewModel.informationBoxHeader != null) && (isPast(new Date(viewModel.informationBoxStartTime)) || viewModel.informationBoxStartTime == null) && (isFuture(new Date(viewModel.informationBoxEndTime)) || viewModel.informationBoxEndTime == null);
    const vipPeriod = getIsVipPeriod();
    const floorplan = getObjectImagesByType(viewModel.images, [ObjectImageType.FloorPlan, ObjectImageType.AltFloorPlan]);

    const pageClass = classnames('object-page', {
        'original': viewModel.concept === ConceptType.JM_ORIGINAL,
        'premium': viewModel.concept === ConceptType.PREMIUM,
        'smarta': viewModel.concept === ConceptType.SMARTA_KVADRAT
    });

    const [isSticky, setIsSticky] = React.useState(false);

    const onStickyChange = ({status}) => {
        setIsSticky(status === Sticky.STATUS_FIXED);
    };

    return (
        <div className={pageClass}>
            {isBookable && viewModel.stageStatus !== StageStatus.VIP && !viewModel.coOwningPossible &&
                <ClickToBuyBanner
                    objectId={viewModel.objectId}
                    objectVipKey={viewModel.vipBookingInvitationKey}
                />
            }
            {(viewModel.coOwningPossible && viewModel.coOwningObjectHeadline && viewModel.coOwningPageUrl) &&
                <CoOwningBanner
                    headline={viewModel.coOwningObjectHeadline}
                    text={viewModel.coOwningObjectText}
                    url={viewModel.coOwningPageUrl}
                />
            }

            {vipPeriod &&
                <VipPeriodBanner
                    startDate={viewModel.vipStart}
                    endDate={viewModel.vipEnd}
                    small
                    onStartPurchase={onVipPeriodClick}
                    purchaseComplete={purchaseComplete}
                    objectId={viewModel.objectId}
                />
            }

            <ImageHeader
                title={viewModel.header}
                images={viewModel.images}
                heroImage={!viewModel.heroImage
                    ? undefined
                    : viewModel.heroImage}
                objectStatus={viewModel.objectStatus}
                concept={viewModel.concept}
                isMobile={isMobile}
                residence={getResidenceSearchResult()}
                user={user}
                galleryType={GalleryType.OBJECT}
                showFavorite
                digitalShowingText={viewModel.digitalShowingText}
                digitalShowingLinkUrl={viewModel.digitalShowingLinkUrl}
                playVideoOnClick
            />

            {viewModel.structuredData && viewModel.structuredData.map((structuredData, index) => {
                return (
                    <StructuredData key={`structured-data-${index}`} structuredData={structuredData}/>
                );
            })}

            <div className="project-page__content">
                <div className="project-nav">
                    <Sticky onStateChange={onStickyChange} innerZ={1010}>
                        <div
                            className={isSticky ?
                                'sticky-wrapper sticky-wrapper--sticky' :
                                'sticky-wrapper'}
                        >
                            <ImageHeaderMenu button={getButton()}>
                                <li>
                                    <A
                                        href="#"
                                        variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                        onClick={onMenuClick('overview', 0)}
                                        attachedEvent={gtmEventsObjectPageStickyClick("overview")}
                                        title={getFormattedMessage('jm.components.ObjectPage.menuFact')}
                                    >
                                        <FormattedMessage id='jm.components.ObjectPage.menuFact'/>
                                    </A>
                                </li>

                                {(viewModel.has360 && !noViews) &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('threeSixty', 1)}
                                            attachedEvent={gtmEventsObjectPageStickyClick("threeSixty")}
                                            title={getFormattedMessage('jm.components.ObjectPage.menuThreeSixty')}
                                        >
                                            <FormattedMessage id='jm.components.ObjectPage.menuThreeSixty'/>
                                        </A>
                                    </li>
                                }

                                {hasShowing(viewModel.showings, viewModel.stageStatus, viewModel.showingText) &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('showings', 1)}
                                            attachedEvent={gtmEventsObjectPageStickyClick("showings")}
                                            title={getFormattedMessage('jm.components.ObjectPage.menuShowings')}
                                        >
                                            <FormattedMessage id='jm.components.ObjectPage.menuShowings'/>
                                        </A>
                                    </li>
                                }
                                {floorplan !== null &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('floorPlan', 1)}
                                            attachedEvent={gtmEventsObjectPageStickyClick("floorplan")}
                                            title={getFormattedMessage('jm.components.ObjectPage.menuFloorplan')}
                                        >
                                            <FormattedMessage id='jm.components.ObjectPage.menuFloorplan'/>
                                        </A>
                                    </li>
                                }
                                <li>
                                    <A
                                        href="#"
                                        variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                        onClick={onMenuClick('images', 3)}
                                        attachedEvent={gtmEventsObjectPageStickyClick("images")}
                                        title={getFormattedMessage('jm.components.ObjectPage.menuImages')}
                                    >
                                        <FormattedMessage id='jm.components.ObjectPage.menuImages'/>
                                    </A>
                                </li>
                                {objects && objects.length > 0 &&
                                    <li>
                                        <A
                                            href="#"
                                            variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                            onClick={onMenuClick('objects', 2)}
                                            attachedEvent={gtmEventsObjectPageStickyClick("pricelist")}
                                            title={getFormattedMessage('jm.components.ObjectPage.menuPricelist')}
                                        >
                                            <FormattedMessage id='jm.components.ObjectPage.menuPricelist'/>
                                        </A>
                                    </li>
                                }
                                <li>
                                    <A
                                        href="#"
                                        variant={viewModel.concept === ConceptType.JM_ORIGINAL ? LinkStyle.DARK_BG : LinkStyle.DEFAULT}
                                        onClick={onMenuClick('map', 4)}
                                        attachedEvent={gtmEventsObjectPageStickyClick("map")}
                                        title={getFormattedMessage('jm.components.ObjectPage.menuMap')}
                                    >
                                        <FormattedMessage id='jm.components.ObjectPage.menuMap'/>
                                    </A>
                                </li>
                            </ImageHeaderMenu>
                        </div>
                    </Sticky>
                </div>

                <div className="mobile-cta">
                    {(viewModel.objectStatus !== ObjectStatus.SOLD && viewModel.objectStatus !== ObjectStatus.BOOKED) &&
                        <UserAgent mobile tablet>
                            {getButton()}
                        </UserAgent>
                    }
                    {showRedirectMessage &&
                        <SiteSpecificContent sites={[SITE.BORATT, SITE.SENIORGARDEN]}>
                            <RedirectMessage />
                        </SiteSpecificContent>
                    }
                </div>

                {informationBoxActive &&
                    <InfoMessage headline={viewModel.informationBoxHeader}>
                        <P dangerouslySetInnerHTML={{__html: sanitizeHtml(viewModel.informationBoxText)}}/>
                    </InfoMessage>
                }

                <MainInfo
                    breadcrumbs={breadcrumbs}
                    header={getResidenceHeadline()}
                    viewModel={viewModel} 
                    quickFactsType={QuickFactsType.OBJECT}
                    scrollTo={() => scrollToReference(scrollRefs.facts)}
                    scrollRefs={scrollRefs}
                />

                {viewModel.hasExternalLinker &&
                    <ExternalExplorerBlock stageId={viewModel.stage} objectId={viewModel.apartmentNumber} />
                }

                {viewModel.has360 &&
                    <div ref={scrollRefs.threeSixty}>
                        <MainGrid noGap fullwidth>
                            <ApartmentViewerPlayer stageId={viewModel.stage} objectName={viewModel.name} />
                        </MainGrid>
                    </div>
                }

                {/*(viewModel.has360 && viewModel.sunstudyVisuado) &&
                    <div>
                        <MainGrid noGap fullwidth>
                            <ApartmentViewerSunStudy stageId={viewModel.stage} objectName={viewModel.name} />
                        </MainGrid>
                    </div>
                */}

                {viewModel.concept !== ConceptType.JM_ORIGINAL && viewModel.uspModel &&
                    <Concept
                        concept={viewModel.concept}
                        uspModel={viewModel.uspModel}
                    />
                }

                <MainGrid fullwidth>
                    <div className="project-information__wrapper" ref={scrollRefs.showings}>
                        <ResidenceAside
                            showings={viewModel.showings}
                            stageStatus={viewModel.stageStatus}
                            isVip={user?.isVip}
                            isLoggedIn={isLoggedIn}
                            smarta={viewModel.concept === ConceptType.SMARTA_KVADRAT}
                            stageId={viewModel.stage}
                            stageName={capitalizeFirstLetter(viewModel.stageName)}
                            showingText={viewModel.showingText}
                            showingLinkText={viewModel.showingLinkText}
                            showingLinkUrl={viewModel.showingLinkUrl}
                            contacts={viewModel.realEstateAgents}
                            links={viewModel.links}
                        />
                    </div>
                </MainGrid>

                {isBookable && !viewModel.coOwningPossible &&
                    <ClickToBuyBanner
                        big
                        objectId={viewModel.objectId}
                        objectVipKey={viewModel.vipBookingInvitationKey}
                    />
                }
                {(viewModel.coOwningPossible && viewModel.coOwningObjectHeadline && viewModel.coOwningPageUrl) &&
                    <CoOwningBanner
                        big
                        headline={viewModel.coOwningObjectHeadline}
                        text={viewModel.coOwningObjectText}
                        url={viewModel.coOwningPageUrl}
                    />
                }
                {vipPeriod &&
                    <VipPeriodBanner startDate={viewModel.vipStart} endDate={viewModel.vipEnd} onStartPurchase={onVipPeriodClick} objectId={viewModel.objectId} purchaseComplete={purchaseComplete} />
                }

                <FloorplanTabber
                    images={viewModel.images}
                    innerRef={scrollRefs.floorPlan}
                    has360={viewModel.has360 && viewModel.sunstudyVisuado}
                    stage={viewModel.stage}
                    name={viewModel.name}
                />

                {viewModel.campaignBlock &&
                    <div className="project-teasers">
                        <CampaignBlock {...viewModel.campaignBlock} />
                    </div>
                }

                <div ref={scrollRefs.images}>
                    <ImageGallery
                        galleryType={GalleryType.OBJECT}
                        images={viewModel.images && viewModel.images.length > 0 ?
                            viewModel.images :
                            []}
                        digitalShowingText={viewModel.digitalShowingText}
                        digitalShowingLinkUrl={viewModel.digitalShowingLinkUrl}
                        putVideoLast
                    />
                </div>

                <InView triggerOnce onChange={bellowFoldCallback}>
                    {({ref}) => (
                        <div ref={ref} className="visibility-sensor"></div>
                    )}
                </InView>

                {(viewModel.swanLabel || viewModel.safeBrf) &&
                    <LabelSection
                        swanLabel={viewModel.swanLabel}
                        swanPageUrl={viewModel.swanPageUrl}
                        safeBrf={viewModel.safeBrf}
                    />
                }

                {
                    <div className="residence-explorer" ref={scrollRefs.objects}>
                        <MainGrid noGap fullwidth>
                            <ResidenceExplorerV2 settings={{
                                constructionForm: viewModel.constructionForm as ResidenceType,
                                currentObjectId: viewModel.apartmentNumber,
                                siteId: viewModel.siteId,
                                stageId: viewModel.stage,
                                stageIds: [...viewModel.residenceExplorerMultiple, ...[viewModel.stage]],
                                stageName: viewModel.stageName,
                                stageStatus: viewModel.stageStatus,
                                pageType: ResidenceExplorerPageType.STAGE,
                                vipStart: viewModel.vipStart,
                                vipEnd: viewModel.vipEnd,
                                hideSoldObjects: viewModel.hideSoldObjects,
                                hideSoldObjectsInExplorer: viewModel.hideSoldObjectsInExplorer,
                                sunStudyExternalUrl: viewModel.sunstudyExternalUrl,
                                hasExplorer: viewModel.hasExplorer,
                            }}/>

                            <PriceListV2
                                disposalForm={viewModel.disposalForm as DisposalForm}
                                constructionForm={viewModel.constructionForm as ResidenceType}
                                isCoOwning={viewModel.coOwningStagePossible}
                                isMultiProject={viewModel.residenceExplorerMultiple && viewModel.residenceExplorerMultiple.length>0}
                                currentNumberOfRooms={viewModel.rooms}
                                objectId={viewModel.apartmentNumber}
                            />
                        </MainGrid>
                    </div>
                }

                {connectedStages.length > 0 &&
                    <AccessibleSlider
                        id="connected-stages"
                        className="project-page__connected-stages"
                        header={getFormattedMessage("jm.components.StagePage.connectedStages", {area: areaName })}
                    >
                        {connectedStages.map((stage) => {
                            return (
                                <ResidenceCard
                                    residence={stage} 
                                    key={`card-${stage.id}`}  
                                    cardType={ResidenceCardType.PROJECT} 
                                    horizontal={connectedStages.length === 1 && !isMobile} 
                                />
                            );
                        })}
                    </AccessibleSlider>
                }

                <div className="project-teasers">
                    <CampaignBlock
                        component="campaign-block"
                        renderStyle=""
                        data={{
                            title: `${capitalizeFirstLetter(viewModel.stageName)}`,
                            text: viewModel.stageDescription,
                            campaignLinkUrl: viewModel.stageUrl,
                            buttonText: getFormattedMessage('jm.components.PromotionBlock.readMoreAboutStage', {projectName: capitalizeFirstLetter(viewModel.stageName)}),
                            //buttonType: CampaignBlockButtonType.READ_MORE,
                            //@ts-expect-error
                            image: (viewModel.stageImages && viewModel.stageImages.length > 0) && viewModel.stageImages[0],
                            theme: 'Light',
                            internalLink: true
                        }}
                    />
                </div>

                <ProjectTimeline timeline={viewModel.timeline} />

                <div ref={scrollRefs.map} className="object-page__map-container">
                    {viewModel.latitude && viewModel.longitude &&
                        <MapWrapper
                            thirdPartyCookie={userCookieSettings.thirdParty}
                            residences={[convertToSearchResult(viewModel)]}
                        />
                    }
                </div>

                {showInterestForm() &&
                    <Container>

                            <div ref={scrollRefs.interest}/>
                            <InterestForm
                                agent={viewModel.realEstateAgents && viewModel.realEstateAgents[0]}
                                stageName={viewModel.header}
                                stageId={viewModel.stage}
                                stageStatus={viewModel.stageStatus}
                                objectId={viewModel.objectId}
                                objectStatus={viewModel.objectStatus}
                                hideMessageField={viewModel.stageStatus === StageStatus.PLANNED || viewModel.stageStatus === StageStatus.VIP}
                                labelPrefix="on-site"
                                interestDone={interestDone}
                                setInterestDone={setInterestDone}
                            />
                    </Container>
                }

                {!viewModel.isMyBookedObject && relatedObjects && relatedObjects.length > 0 &&
                    <AccessibleSlider
                        header={getFormattedMessage('jm.components.ObjectPage.suggestionSliderHeadline')}
                        link={getCurrentSite() === SITE.FI ?
                            `${SITE_URLS.searchUrl}#object` :
                            relatedObjectsUrl}
                        linkText={getFormattedMessage('jm.components.ObjectPage.seeAll')}
                    >
                        {relatedObjects.map((object) => {
                            return (
                                <ResidenceCard
                                    residence={object} 
                                    key={`card-${object.id}`}  
                                    cardType={ResidenceCardType.OBJECT} 
                                    horizontal={relatedObjects.length === 1 && !isMobile}
                                />
                            );
                        })}
                    </AccessibleSlider>
                }
                {viewModel.siteSpecificContentArea !== null &&
                    <ContentArea
                        id="corporate-links"
                        className="corporate-links-area"
                        hasGrid
                        blocks={viewModel.siteSpecificContentArea}
                    />
                }
            </div>
        </div>
    )
};
